import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import VersionDrawer from "./VersionDrawer";
import { File } from "@/types/app/file";
import { useVersionTableActions } from "../Tables/Versions/actions";
import { Action } from "../Tables/ActionsColumn/ActionsMenu";
import { useHotkeys } from "react-hotkeys-hook";
import { useRouter } from "next/router";

interface VersionDrawerContextProps {
  file?: File;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedVersion: File | null;
  setSelectedVersion: React.Dispatch<React.SetStateAction<File | null>>;

  // Allow callers to control the z-index of the version drawer, i.e.
  // move it above dialogs.
  overDialog: boolean;
  setOverDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export const VersionDrawerContext = createContext<VersionDrawerContextProps | undefined>(undefined);

// This context provider is for handling opening the VersionDrawer from the Open button within the VersionPopover
// TODO: centralize the coordination of the drawer state
export const VersionDrawerContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<File | null>(null);
  const [overDialog, setOverDialog] = useState(false);

  // This is quite ugly; the version that this drawer context uses should remain static.
  // The only purpose of selected version state is for the parent to render the deployment dialog that's triggered by
  // setting the selected version. What we should aim to do instead, is have a DS like a stack that maintains
  // the versions that are open in the drawer.
  const actions: Action<File>[] = useVersionTableActions(selectedVersion ?? undefined, setSelectedVersion);
  const router = useRouter();
  const previousPathname = useRef(router.pathname);

  // Close drawer only when pathname changes (not just query params)
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      // Parse the URL to get just the pathname
      const urlObj = new URL(url, window.location.origin);
      const newPathname = urlObj.pathname;

      // Only close if the pathname is different
      if (newPathname !== previousPathname.current) {
        setOpen(false);
        previousPathname.current = newPathname;
      }
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router.events, setOpen]);

  // Update the ref when the pathname changes
  useEffect(() => {
    previousPathname.current = router.pathname;
  }, [router.pathname]);

  useHotkeys("esc", () => {
    setOpen(false);
  });

  return (
    <>
      <VersionDrawerContext.Provider
        value={{ open, setOpen, selectedVersion, setSelectedVersion, overDialog, setOverDialog }}
      >
        {children}
        <VersionDrawer
          version={selectedVersion}
          // TODO: get this from the file
          monitoringEvaluators={undefined}
          open={open}
          onClose={() => {
            setOpen(false);

            // Reset z-index of drawer after closing animation.
            setTimeout(() => {
              setOverDialog(false);
            }, 300);
          }}
          onNext={undefined}
          onPrevious={undefined}
          actions={actions}
          className={overDialog ? "z-[1002]" : ""} // Dialogs are z-[1001]
        />
      </VersionDrawerContext.Provider>
    </>
  );
};

/** A context hook to render the drawer to show Logs, etc.
 *
 * Initially I'm just designing this to be used in Prompt Editor, so have just done
 * the simplest dumbest thing possible. However, I think this could be made
 * more generic and reusable.
 *
 * Ideas:
 * - use this for everywhere we use the drawers
 * - load any content
 * - keep a stack of drawers, and manage them
 */
export const useVersionDrawer = () => {
  const context = useContext(VersionDrawerContext);
  if (context === undefined) {
    throw new Error("useVersionsDrawer must be used within a VersionDrawerContextProvider");
  }
  return context;
};
